import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Controller} from 'react-hook-form'
import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    InputGroup,
    InputRightElement,
    IconButton,
    Select,
    Checkbox,
    Text,
    InputLeftElement,
    Image,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {Textarea, ViewIcon, ViewOffIcon} from '@chakra-ui/icons'
import {
    formControlStyle,
    formErrorMessageStyle,
    formInputErrorStyle,
    formInputStyle,
    formLabelStarStyle,
    formLabelStyle,
    giftCardLabelStyle,
    mobilePrependText
} from '../register/index.style'
import {pxToRem} from '../../utills/pxToRem'
import {promotionCodeLabelStyle, tagIconStyle} from '../order-summary/index.style'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import colors from '../../theme/foundations/colors'
import {fonts} from '../../theme/foundations/fonts'

const Field = ({
    name,
    label,
    formLabel,
    type = 'text',
    options = [],
    rules = {},
    error,
    placeholder,
    inputProps,
    control,
    autoComplete,
    defaultValue,
    helpText,
    children,
    inputRef,
    styles = {},
    isContactInfo = false,
    isAccountAddress = false,
    contactInfoError = '',
    disabled = false,
    border,
    readOnly = false,
    form = {}
}) => {
    const {formatMessage} = useIntl()
    const [hidePassword, setHidePassword] = useState(true)
    const inputType =
        type === 'password' && hidePassword ? 'password' : type === 'password' ? 'text' : type

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue}
            render={({field: {onChange, value, ref}}) => {
                const _inputProps =
                    typeof inputProps === 'function' ? inputProps({value, onChange}) : inputProps

                return (
                    <FormControl id={name} isInvalid={error} sx={formControlStyle}>
                        {!['checkbox', 'radio', 'hidden'].includes(type) &&
                            (formLabel || (
                                <FormLabel
                                    sx={{
                                        ...(label === 'Promotion Code'
                                            ? promotionCodeLabelStyle
                                            : label === 'Your Name'
                                            ? giftCardLabelStyle
                                            : formLabelStyle)
                                    }}
                                    display={type === 'textarea' ? 'inline-block' : 'block'}
                                    marginBottom={
                                        isContactInfo
                                            ? pxToRem(5)
                                            : [
                                                  `${pxToRem(5)} !important`,
                                                  `${pxToRem(5)} !important`,
                                                  `${pxToRem(10)} !important`,
                                                  `${pxToRem(10)} !important`
                                              ]
                                    }
                                >
                                    {label}
                                    {type !== 'date' &&
                                        label !== 'Promotion Code' &&
                                        rules.required &&
                                        label !== 'Address Nickname' && (
                                            <Text sx={formLabelStarStyle}>*</Text>
                                        )}
                                    {type == 'textarea' && (
                                        <span style={{fontSize: pxToRem(10), fontStyle: 'italic'}}>
                                            {' '}
                                            ( Please do not use special characters and emojis, they
                                            will be removed )
                                        </span>
                                    )}
                                </FormLabel>
                            ))}

                        <InputGroup>
                            {/* prepend +1 in mobile phone */}
                            {type === 'tel' && (
                                <InputLeftElement
                                    height={[pxToRem(35), pxToRem(35), pxToRem(40), pxToRem(40)]}
                                >
                                    <Text sx={mobilePrependText}>+1</Text>
                                </InputLeftElement>
                            )}

                            {[
                                'text',
                                'password',
                                'email',
                                'phone',
                                'tel',
                                'date',
                                'number'
                            ].includes(type) && (
                                <Input
                                    ref={(node) => {
                                        ref(node)
                                        if (inputRef) inputRef.current = node
                                    }}
                                    onChange={(e) => {
                                        const val = e.target.value
                                        const filtered =
                                            type === 'tel' || label === 'Zip Code'
                                                ? val.replace(/[^0-9]/g, '')
                                                : val
                                        onChange(filtered)
                                    }}
                                    value={value}
                                    type={inputType}
                                    placeholder={placeholder}
                                    autoComplete={autoComplete}
                                    maxLength={type == 'tel' ? 14 : label === 'Zip Code' ? 5 : 50}
                                    readOnly={readOnly}
                                    {..._inputProps}
                                    isInvalid={!!error}
                                    errorBorderColor="cav_red"
                                    sx={{
                                        ...(contactInfoError
                                            ? formInputErrorStyle
                                            : formInputStyle),
                                        ...(error && {
                                            _focus: {
                                                backgroundColor: colors.whiteRed[20],
                                                borderColor: 'cav_error_red',
                                                boxShadow: 'none',
                                                outline: 'none'
                                            }
                                        }),
                                        ...(disabled && {
                                            _disabled: {
                                                opacity: 1,
                                                cursor: 'default'
                                            }
                                        })
                                    }}
                                    {...(border ? {border} : {})}
                                    {...(disabled ? {disabled} : {})}
                                />
                            )}

                            {/* textarea on gift card purchase form */}
                            {type == 'textarea' && (
                                <>
                                    <Textarea
                                        rows={5}
                                        cols={50}
                                        ref={(node) => {
                                            ref(node)
                                            if (inputRef) inputRef.current = node
                                        }}
                                        onChange={(e) => {
                                            let val = e.target.value

                                            // Remove emojis (most common Unicode ranges)
                                            val = val.replace(
                                                /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF]|[\u2011-\u26FF])/gu,
                                                ''
                                            )
                                            //    const cleaned = removeEmojis(e.target.value);
                                            // val = val.replace(/(\p{Extended_Pictographic}|\uFE0F)/gu, '');

                                            onChange(val)
                                        }}
                                        value={value}
                                        type={inputType}
                                        placeholder={placeholder}
                                        autoComplete={autoComplete}
                                        maxLength={250}
                                        readOnly={readOnly}
                                        {..._inputProps}
                                        isInvalid={!!error}
                                        errorBorderColor="cav_red"
                                        sx={{
                                            ...(contactInfoError
                                                ? formInputErrorStyle
                                                : formInputStyle),
                                            ...(error && {
                                                _focus: {
                                                    backgroundColor: colors.whiteRed[20],
                                                    borderColor: 'cav_error_red',
                                                    boxShadow: 'none',
                                                    outline: 'none'
                                                }
                                            })
                                        }}
                                    />
                                </>
                            )}

                            {type === 'hidden' && (
                                <input
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    type="hidden"
                                    {..._inputProps}
                                />
                            )}
                            {type === 'password' && (
                                <InputRightElement>
                                    <IconButton
                                        variant="ghost"
                                        aria-label={
                                            hidePassword ? 'Show password' : 'Hide password'
                                        }
                                        icon={
                                            hidePassword ? (
                                                <ViewIcon color="cav_gray_eye" />
                                            ) : (
                                                <ViewOffIcon color="cav_gray_eye" />
                                            )
                                        }
                                        onClick={() => setHidePassword(!hidePassword)}
                                        sx={{
                                            color: 'cav_gray_eye',
                                            '&:hover': {
                                                backgroundColor: 'transparent'
                                            }
                                        }}
                                    />
                                </InputRightElement>
                            )}

                            {type === 'select' && (
                                <Select
                                    ref={ref}
                                    onChange={onChange}
                                    value={value}
                                    placeholder={placeholder}
                                    {..._inputProps}
                                    borderColor={'black'}
                                >
                                    {options.map((opt) => (
                                        <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Select>
                            )}

                            {type === 'checkbox' && (
                                <Checkbox
                                    ref={ref}
                                    onChange={(e) => onChange(e.target.checked)}
                                    isChecked={value}
                                    {..._inputProps}
                                    sx={{
                                        '& .chakra-checkbox__control[data-checked]': {
                                            backgroundColor: 'cav_calltoaction_darkred',
                                            borderColor: 'cav_calltoaction_darkred',
                                            color: 'white'
                                        },
                                        '& .chakra-checkbox__label': {
                                            color: 'cav_light_input_gray',
                                            fontFamily: fonts.primary,
                                            fontSize: [
                                                pxToRem(12),
                                                pxToRem(12),
                                                pxToRem(13),
                                                pxToRem(13)
                                            ],
                                            fontWeight: '400',
                                            lineHeight: 'normal',
                                            letterSpacing: pxToRem(1)
                                        }
                                    }}
                                >
                                    {formLabel || label}
                                </Checkbox>
                            )}

                            {children}
                        </InputGroup>

                        {error && type !== 'hidden' && (
                            <Flex
                                flexDirection="row"
                                alignItems={'center'}
                                gap="5px"
                                mt={{base: '5px', md: '10px'}}
                            >
                                <Image
                                    src={getAssetUrl('static/img/header/error-symbol.svg')}
                                    alt="Error symbol"
                                    sx={{w: '13px', h: '13px', mt: '8px'}}
                                />
                                <FormErrorMessage sx={{...formErrorMessageStyle, ...styles}}>
                                    {error.message}
                                </FormErrorMessage>
                            </Flex>
                        )}

                        {type == 'textarea' && (
                            <span
                                style={{
                                    fontSize: pxToRem(12),
                                    fontWeight: 500,
                                    fontFamily: fonts.primary,
                                    display: 'inline-block',
                                    marginTop: pxToRem(0)
                                }}
                            >
                                {formatMessage(
                                    {
                                        defaultMessage:
                                            'You have {count} characters left out of 250',
                                        id: 'gift_cart_purcahse.message.remaining.label'
                                    },
                                    {
                                        count: 250 - form?.watch('message')?.length
                                    }
                                )}
                            </span>
                        )}
                        {helpText}
                    </FormControl>
                )
            }}
        />
    )
}

Field.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    autoComplete: PropTypes.string,
    formLabel: PropTypes.any,
    type: PropTypes.oneOf([
        'text',
        'number',
        'password',
        'email',
        'phone',
        'tel',
        'select',
        'checkbox',
        'hidden'
    ]),
    options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    control: PropTypes.object,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    children: PropTypes.any,
    inputRef: PropTypes.object,
    styles: PropTypes.object,
    isContactInfo: PropTypes.bool,
    isAccountAddress: PropTypes.bool,
    contactInfoError: PropTypes.string,
    border: PropTypes.number,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    form: PropTypes.object
}

export default Field
